/**
 * Configuration.
 *
 * Rendered from the server's own registry — each setting declares its label,
 * its group and the control it wants — so adding a setting on the server makes
 * it appear here without a matching change in the browser.
 */
import { useEffect, useState } from 'react';
import { endpoints, type ConfigurationSetting } from '../../lib/api';
import { Alert, Button, Card, Field, Input, Select, Skeleton } from '../../components/ui';

const GROUP_LABELS: Record<ConfigurationSetting['group'], string> = {
  PROPOSAL: 'Proposal defaults',
  COMPANY: 'Company contact',
  TAX: 'Tax',
  SYSTEM: 'System',
};

export function ConfigurationAdmin({ onError }: { onError(message: string): void }) {
  const [settings, setSettings] = useState<ConfigurationSetting[] | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = () =>
    endpoints
      .adminConfiguration()
      .then((result) => {
        setSettings(result.configuration);
        setDraft({});
      })
      .catch((caught) => onError(String(caught)));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!settings) return <Skeleton className="h-64 w-full" />;

  const dirty = Object.keys(draft).length > 0;
  const valueOf = (setting: ConfigurationSetting) =>
    setting.key in draft ? draft[setting.key] : setting.value;

  const save = async () => {
    setBusy(true);
    setNotice(null);
    try {
      await endpoints.updateConfiguration(draft);
      await load();
      setNotice('Settings saved.');
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  const groups = [...new Set(settings.map((s) => s.group))];

  return (
    <Card
      title="Configuration"
      description="Application defaults. Every value is validated against its declared type before it is stored."
      actions={
        dirty ? (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void save()} loading={busy}>
              Save {Object.keys(draft).length} change
              {Object.keys(draft).length === 1 ? '' : 's'}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setDraft({})} disabled={busy}>
              Discard
            </Button>
          </div>
        ) : undefined
      }
    >
      {notice && (
        <div className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}

      {groups.map((group) => (
        <section key={group} className="mt-6 first:mt-0">
          <h3 className="text-[12px] font-semibold uppercase tracking-wide text-muted">
            {GROUP_LABELS[group]}
          </h3>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            {settings
              .filter((setting) => setting.group === group)
              .map((setting) => (
                <SettingControl
                  key={setting.key}
                  setting={setting}
                  value={valueOf(setting)}
                  changed={setting.key in draft}
                  onChange={(value) =>
                    setDraft((current) => ({ ...current, [setting.key]: value }))
                  }
                />
              ))}
          </div>
        </section>
      ))}

      <p className="mt-6 text-[12px] text-muted">
        Changes are applied as a set: every value is checked, then the rules that span settings
        run against the result, and only then is anything written. That is why the default
        currency cannot be saved to something the allowed-currency list does not include.
      </p>
    </Card>
  );
}

function SettingControl({
  setting,
  value,
  changed,
  onChange,
}: {
  setting: ConfigurationSetting;
  value: unknown;
  changed: boolean;
  onChange(value: unknown): void;
}) {
  const id = `cfg-${setting.key}`;
  const hint = [
    setting.description,
    changed ? 'Unsaved' : setting.isDefault ? 'Default' : null,
    setting.advanced ? 'Advanced' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  if (setting.control === 'BOOLEAN') {
    return (
      <label className="flex items-start gap-3 text-[13px]">
        <input
          id={id}
          type="checkbox"
          className="mt-1 size-4 shrink-0 accent-teal"
          checked={Boolean(value)}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>
          <span className="block font-medium text-ink">{setting.label}</span>
          <span className="block text-[12px] text-muted">{hint}</span>
        </span>
      </label>
    );
  }

  if (setting.control === 'MULTI_SELECT') {
    const selected = Array.isArray(value) ? (value as string[]) : [];
    return (
      <Field label={setting.label} htmlFor={id} hint={hint}>
        <div id={id} className="flex flex-wrap gap-3 pt-1">
          {(setting.options ?? []).map((option) => (
            <label key={option.value} className="flex items-center gap-1.5 text-[13px]">
              <input
                type="checkbox"
                className="size-4 accent-teal"
                checked={selected.includes(option.value)}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...selected, option.value]
                      : selected.filter((v) => v !== option.value),
                  )
                }
              />
              {option.label}
            </label>
          ))}
        </div>
      </Field>
    );
  }

  if (setting.control === 'SELECT') {
    return (
      <Field label={setting.label} htmlFor={id} hint={hint}>
        <Select id={id} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
          {(setting.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>
    );
  }

  return (
    <Field label={setting.label} htmlFor={id} hint={hint}>
      <Input
        id={id}
        type={setting.control === 'NUMBER' ? 'number' : 'text'}
        value={String(value ?? '')}
        onChange={(event) =>
          onChange(
            setting.control === 'NUMBER'
              ? // Sent as a number, because the server refuses a numeric setting
                // that arrives as a string rather than coercing it.
                Number(event.target.value)
              : event.target.value,
          )
        }
      />
    </Field>
  );
}
